import { ReactNode } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { AdminPostLeadReqDto } from "service/api/estabrookSchemas";
import { makeSxStyles } from "ui";
import useAddressAutocomplete from "@/hooks/use-autocomplete";
import CrmDetailsFields from "@/shared/components/crm/CrmDetailsFields";
import FieldRow from "@/shared/components/FieldRow";
import { handlePhoneChange } from "@/shared/utils/phoneUtils";

interface LeadFormProps {
  leadForm: FormikProps<AdminPostLeadReqDto>;
  // Shown under the "Lead Info" title on the edit page (e.g. "Created … via …").
  leadInfoSubtitle?: string;
  // Rendered at the end of the Lead Info section (e.g. the edit page's delete).
  leadInfoFooter?: ReactNode;
}

const LeadForm = ({
  leadForm,
  leadInfoSubtitle,
  leadInfoFooter,
}: LeadFormProps) => {
  const styles = useStyles();
  const { getPlacePredictions, placePredictions } = useAddressAutocomplete();
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = leadForm;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leadInfoHeader}>
        <Typography sx={styles.sectionTitle}>Lead Info</Typography>
        {leadInfoSubtitle && (
          <Typography sx={styles.subtitle}>{leadInfoSubtitle}</Typography>
        )}
      </Box>

      <FieldRow label="First Name" required>
        <TextField
          id="firstName"
          value={values.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.firstName && !!errors.firstName}
          helperText={touched.firstName && errors.firstName}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Last Name" required>
        <TextField
          id="lastName"
          value={values.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.lastName && !!errors.lastName}
          helperText={touched.lastName && errors.lastName}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Email Address" required>
        <TextField
          id="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.email && !!errors.email}
          helperText={touched.email && errors.email}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Phone Number" required>
        <TextField
          id="phoneNumber"
          value={values.phoneNumber}
          onChange={(e) => {
            setFieldValue("phoneNumber", handlePhoneChange(e.target.value));
            // Skip the stale re-validation (see Service Address note below).
            setFieldTouched("phoneNumber", true, false);
          }}
          onBlur={handleBlur}
          error={touched.phoneNumber && !!errors.phoneNumber}
          helperText={touched.phoneNumber && errors.phoneNumber}
          fullWidth
        />
      </FieldRow>
      <FieldRow label="Service Address" required>
        <Autocomplete
          freeSolo
          filterOptions={(x) => x}
          inputValue={values.address ?? ""}
          options={placePredictions.map((prediction) => ({
            label: prediction.description,
            value: prediction,
          }))}
          onInputChange={(_evt, newValue, reason) => {
            // "input" = user typing; "clear" = the built-in clear (X) button,
            // which sends "". Both keep the form in sync and refresh
            // predictions. Selecting a prediction comes through onChange.
            if (reason === "input" || reason === "clear") {
              // setFieldValue validates with the new value; setFieldTouched must
              // NOT re-validate (it would run against the stale value and wipe
              // the just-computed error — e.g. clearing a required address).
              setFieldValue("address", newValue);
              setFieldTouched("address", true, false);
              getPlacePredictions({ input: newValue.trim() });
            }
          }}
          onChange={(_evt, value) => {
            // `null` = cleared via the clear button; object = prediction picked.
            if (value == null) {
              setFieldValue("address", "");
              setFieldTouched("address", true, false);
            } else if (typeof value === "object") {
              setFieldValue("address", String(value.label));
              setFieldTouched("address", true, false);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              id="address"
              data-testid="address"
              error={touched.address && !!errors.address}
              helperText={touched.address && errors.address}
              onBlur={handleBlur}
              fullWidth
            />
          )}
        />
      </FieldRow>

      {leadInfoFooter}

      <CrmDetailsFields
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    mt: "20px",
    pb: "40px",
  },
  leadInfoHeader: {
    mb: "20px",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "20px",
  },
  subtitle: {
    fontSize: "13px",
    color: "#c1c7ce",
    mt: "4px",
  },
}));

export default LeadForm;
